import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  RefreshCw,
  Loader2,
  Zap,
  ChevronDown,
  ChevronRight,
  Users,
  Calendar,
  Mail,
  CheckCircle,
  Clock,
  Link2,
  Copy,
  ExternalLink,
  RotateCcw,
  ArrowLeftRight,
  Wrench
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format, parseISO, isBefore } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useInstallmentsCopy } from '@/hooks/useInstallmentsCopy';

const statusColors = {
  confirmed: 'bg-emerald-500/10 text-emerald-400',
  pending: 'bg-brand-500/10 text-brand-400',
  completed: 'bg-brand-500/10 text-brand-400',
  cancelled: 'bg-red-500/10 text-red-400',
  defaulted: 'bg-red-500/10 text-red-400',
  ticketed: 'bg-brand-500/10 text-brand-400',
  active: 'bg-emerald-500/10 text-emerald-400',
  past_due: 'bg-orange-500/10 text-orange-400',
  canceled: 'bg-red-500/10 text-red-400',
  stripe_unavailable: 'bg-brand-500/10 text-brand-400'
};

const frequencyLabels = { weekly: 'Weekly', biweekly: 'Biweekly' };

function formatCurrency(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format((cents ?? 0) / 100);
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return '—';
  }
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'MMM d, h:mm a');
  } catch {
    return '—';
  }
}

export default function AdminAllZeroTripsModal({ open, onClose }) {
  const ic = useInstallmentsCopy();
  const [data, setData] = useState({ trips: [], users: {}, total_collected_cents: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [repairingId, setRepairingId] = useState(null);

  // Create Quote widget state
  const [showCreateQuote, setShowCreateQuote] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    sale_price_dollars: '',
    booking_reference: '',
    departure_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'USD'
  });
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in');
        return;
      }
      const res = await fetch('/api/admin/defera-zero/all-trips', {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 403) toast.error('Admin access required');
        else toast.error(err.error || 'Failed to load');
        return;
      }
      const json = await res.json();
      setData({
        trips: json.trips || [],
        users: json.users || {},
        total_collected_cents: json.total_collected_cents ?? 0
      });
    } catch (e) {
      toast.error(e.message || 'Failed to load');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleRepairSubscription = useCallback(
    async (tripId) => {
      if (
        !window.confirm(
          'Repair this Defera Zero subscription in Stripe?\n\nThis syncs trip total from the booking to Stripe metadata, then sets the recurring charge to match the remaining balance and payment schedule (same logic as the customer app).'
        )
      ) {
        return;
      }
      setRepairingId(tripId);
      try {
        const {
          data: { session }
        } = await supabase.auth.getSession();
        if (!session) {
          toast.error('Please sign in');
          return;
        }
        const res = await fetch('/api/admin/defera-zero/repair-subscription', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ booking_id: tripId })
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Repair failed');
        const oldAmt =
          json.old_unit_amount_cents != null ? formatCurrency(json.old_unit_amount_cents) : '—';
        const newAmt =
          json.new_unit_amount_cents != null ? formatCurrency(json.new_unit_amount_cents) : '—';
        if (json.action === 'updated') {
          toast.success(`Stripe recurring charge: ${oldAmt} → ${newAmt}`);
        } else if (json.action === 'noop') {
          if (json.skip_reason === 'within_one_cent') {
            toast.success(
              `No Stripe change — recurring charge already within 1¢ of schedule (${newAmt}).`
            );
          } else {
            toast.success(`Already aligned (${newAmt} / charge)`);
          }
        } else if (json.action === 'cancelled') {
          toast.success('Subscription cancelled (nothing left owed)');
        } else {
          toast.success('Repair completed');
        }
        await fetchAll();
      } catch (e) {
        toast.error(e.message || 'Repair failed');
      } finally {
        setRepairingId(null);
      }
    },
    [fetchAll]
  );

  const handleCreateQuote = useCallback(async () => {
    const saleDollars = parseFloat(quoteForm.sale_price_dollars);
    if (!quoteForm.sale_price_dollars || isNaN(saleDollars) || saleDollars <= 0) {
      toast.error('Enter a valid sale amount (dollars)');
      return;
    }
    setQuoteLoading(true);
    setQuoteResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in');
        return;
      }
      const res = await fetch('/api/admin/slicepay/create-quote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          sale_price_dollars: saleDollars,
          booking_reference: quoteForm.booking_reference?.trim() || undefined,
          departure_date: quoteForm.departure_date || undefined,
          currency: quoteForm.currency || 'USD'
        })
      });
      const json = await res.json();
      if (!res.ok) {
        toast.error(json.error || 'Failed to create quote');
        return;
      }
      if (json.success && json.embedded_flow_url) {
        setQuoteResult({
          url: json.embedded_flow_url,
          booking_reference: json.booking_reference,
          sale_price: json.sale_price,
          currency: json.currency
        });
        toast.success('Quote link generated');
      } else {
        toast.error(json.error || 'Failed to create quote');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to create quote');
    } finally {
      setQuoteLoading(false);
    }
  }, [quoteForm]);

  const copyQuoteLink = useCallback(() => {
    if (!quoteResult?.url) return;
    navigator.clipboard.writeText(quoteResult.url);
    toast.success('Link copied to clipboard');
  }, [quoteResult]);

  const handleResetQuote = useCallback(() => {
    setQuoteForm({
      sale_price_dollars: '',
      booking_reference: '',
      departure_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      currency: 'USD'
    });
    setQuoteResult(null);
    toast.success('Create Quote form reset');
  }, []);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  const byUser = React.useMemo(() => {
    const map = {};
    (data.trips || []).forEach((t) => {
      if (!map[t.user_id]) map[t.user_id] = [];
      map[t.user_id].push(t);
    });
    return map;
  }, [data.trips]);

  const userList = Object.keys(byUser);

  if (!open) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex flex-col bg-[#050505]"
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 sm:px-6 border-b border-white/10 bg-[#0a0a0a]/80 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <h2 className="text-lg sm:text-xl font-light text-white">All Defera Zero Trips</h2>
            <span className="text-white/40 text-sm font-normal">
              {data.trips?.length || 0} trip{data.trips?.length !== 1 ? 's' : ''} • {userList.length} user{userList.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchAll}
              disabled={isLoading}
              className="text-white/50 hover:text-white hover:bg-white/5 rounded-full border border-white/10"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ml-1.5 hidden sm:inline">Refresh</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white/50 hover:text-white hover:bg-white/5 rounded-full border border-white/10"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 sm:py-6">
          {isLoading && !data.trips?.length ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 animate-spin text-white/40" />
            </div>
          ) : (
            <div className="space-y-6 max-w-4xl mx-auto">
              {/* Create Quote widget - always visible for admins */}
              <div className="rounded-2xl bg-black/40 border border-white/10 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowCreateQuote((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4 text-left hover:bg-black/30 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-cyan-400" />
                    <span className="text-white/90 font-medium">Create Quote</span>
                    <span className="text-white/40 text-sm font-normal">{ic.adminCustomInstallmentLinkHint}</span>
                  </div>
                  {showCreateQuote ? (
                    <ChevronDown className="w-4 h-4 text-white/40" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-white/40" />
                  )}
                </button>
                <AnimatePresence>
                  {showCreateQuote && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-white/10 overflow-hidden"
                    >
                      <div className="p-4 sm:p-5 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-white/50 text-xs font-normal uppercase tracking-wider mb-1.5">
                              Sale Amount ($)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="e.g. 500.00"
                              value={quoteForm.sale_price_dollars}
                              onChange={(e) => setQuoteForm((f) => ({ ...f, sale_price_dollars: e.target.value }))}
                              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 focus:border-cyan-500/50"
                            />
                          </div>
                          <div>
                            <label className="block text-white/50 text-xs font-normal uppercase tracking-wider mb-1.5">
                              Booking Reference
                            </label>
                            <input
                              type="text"
                              placeholder="Optional (auto-generated if empty)"
                              value={quoteForm.booking_reference}
                              onChange={(e) => setQuoteForm((f) => ({ ...f, booking_reference: e.target.value }))}
                              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 focus:border-cyan-500/50"
                            />
                          </div>
                          <div>
                            <label className="block text-white/50 text-xs font-normal uppercase tracking-wider mb-1.5">
                              Departure Date
                            </label>
                            <input
                              type="date"
                              value={quoteForm.departure_date}
                              onChange={(e) => setQuoteForm((f) => ({ ...f, departure_date: e.target.value }))}
                              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white focus:outline-none focus:ring-1 focus:ring-cyan-500/50 focus:border-cyan-500/50"
                            />
                          </div>
                          <div>
                            <label className="block text-white/50 text-xs font-normal uppercase tracking-wider mb-1.5">
                              Currency
                            </label>
                            <select
                              value={quoteForm.currency}
                              onChange={(e) => setQuoteForm((f) => ({ ...f, currency: e.target.value }))}
                              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white focus:outline-none focus:ring-1 focus:ring-cyan-500/50 focus:border-cyan-500/50"
                            >
                              <option value="USD">USD</option>
                            </select>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                          <Button
                            onClick={handleCreateQuote}
                            disabled={quoteLoading}
                            className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30 rounded-lg px-4 py-2 shrink-0"
                          >
                            {quoteLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Generate Link'
                            )}
                          </Button>
                          <Button
                            onClick={handleResetQuote}
                            disabled={quoteLoading}
                            variant="ghost"
                            className="text-white/50 hover:text-white hover:bg-white/5 border border-white/10 rounded-lg px-4 py-2 shrink-0"
                          >
                            <RotateCcw className="w-4 h-4 mr-1.5 inline" />
                            Reset
                          </Button>
                          {quoteResult && (
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <input
                                type="text"
                                readOnly
                                value={quoteResult.url}
                                className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white/80 text-sm truncate"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={copyQuoteLink}
                                className="text-white/60 hover:text-white hover:bg-white/5 rounded-lg shrink-0"
                                title="Copy link"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(quoteResult.url, '_blank')}
                                className="text-white/60 hover:text-white hover:bg-white/5 rounded-lg shrink-0"
                                title="Open link"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                        {quoteResult && (
                          <p className="text-white/40 text-xs">
                            Ref: {quoteResult.booking_reference} • {quoteResult.sale_price}¢ {quoteResult.currency}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {!data.trips?.length ? (
                <div className="text-center py-24">
                  <Zap className="w-12 h-12 mx-auto text-white/20 mb-4" />
                  <p className="text-white/60 font-normal">No Defera Zero trips yet</p>
                </div>
              ) : (
                <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-xl bg-black/40 border border-white/10 p-3 sm:p-4">
                  <p className="text-white/40 text-xs font-normal uppercase tracking-wider">Trips</p>
                  <p className="text-white text-lg sm:text-xl font-light mt-0.5">{data.trips?.length || 0}</p>
                </div>
                <div className="rounded-xl bg-black/40 border border-white/10 p-3 sm:p-4">
                  <p className="text-white/40 text-xs font-normal uppercase tracking-wider">Users</p>
                  <p className="text-white text-lg sm:text-xl font-light mt-0.5">{userList.length}</p>
                </div>
                <div className="rounded-xl bg-black/40 border border-white/10 p-3 sm:p-4">
                  <p className="text-white/40 text-xs font-normal uppercase tracking-wider">Total Collected</p>
                  <p className="text-cyan-400 text-lg sm:text-xl font-light mt-0.5">
                    {formatCurrency(data.total_collected_cents)}
                  </p>
                </div>
                <div className="rounded-xl bg-black/40 border border-white/10 p-3 sm:p-4">
                  <p className="text-white/40 text-xs font-normal uppercase tracking-wider">Active</p>
                  <p className="text-white text-lg sm:text-xl font-light mt-0.5">
                    {(data.trips || []).filter((t) => t.remaining_cents > 0 && t.status !== 'cancelled').length}
                  </p>
                </div>
              </div>

              {/* By User */}
              <div>
                <h3 className="text-white/90 font-normal mb-4 flex items-center gap-2">
                  <Users className="w-4 h-4 text-white/50" />
                  By User
                </h3>
                <div className="space-y-3">
                  {userList.map((userId) => {
                    const trips = byUser[userId] || [];
                    const userInfo = data.users[userId] || {};
                    const totalCollected = trips.reduce((s, t) => s + (t.paid_amount_cents || 0), 0);
                    const isExpanded = expandedUser === userId;

                    return (
                      <div
                        key={userId}
                        className="rounded-2xl bg-black/40 border border-white/10 overflow-hidden"
                      >
                        <button
                          type="button"
                          onClick={() => setExpandedUser(isExpanded ? null : userId)}
                          className="w-full flex items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4 text-left hover:bg-black/30 transition-colors"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-white/40 flex-shrink-0" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-white/40 flex-shrink-0" />
                            )}
                            <Mail className="w-4 h-4 text-white/30 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <span className="text-white/90 text-sm sm:text-base truncate block">
                                {userInfo.full_name || userInfo.email || 'Unknown'}
                              </span>
                              {userInfo.full_name && userInfo.email && (
                                <span className="text-white/40 text-xs truncate block">{userInfo.email}</span>
                              )}
                            </div>
                            <span className="text-white/40 text-xs flex-shrink-0">
                              {trips.length} trip{trips.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <p className="text-cyan-400 text-sm font-normal">{formatCurrency(totalCollected)}</p>
                            <p className="text-white/30 text-[10px] uppercase">Collected</p>
                          </div>
                        </button>

                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="border-t border-white/10"
                            >
                              <div className="p-4 sm:p-5 space-y-3">
                                {trips.map((trip) => {
                                  const isTripExpanded = expandedTrip === trip.id;
                                  return (
                                    <div
                                      key={trip.id}
                                      className="rounded-xl bg-black/30 border border-white/10 overflow-hidden"
                                    >
                                      <button
                                        type="button"
                                        onClick={() => setExpandedTrip(isTripExpanded ? null : trip.id)}
                                        className="w-full flex flex-wrap items-start justify-between gap-2 p-3 sm:p-4 text-left hover:bg-black/20 transition-colors"
                                      >
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            {(trip.change_flight_history?.length > 0 || trip.archived_flight_data) && (
                                              <span className="flex items-center text-brand-400/90" title="Flight changed by customer">
                                                <ArrowLeftRight className="w-3.5 h-3.5" />
                                              </span>
                                            )}
                                            <span className="text-white/90 font-medium">{trip.route}</span>
                                            <span
                                              className={`px-2 py-0.5 rounded-full text-[10px] font-normal ${statusColors[trip.status] || statusColors[trip.subscription_status] || 'bg-white/10 text-white/60'}`}
                                            >
                                              {trip.status}
                                            </span>
                                            <span className="text-white/40 text-xs">Ref: {trip.trip_reference}</span>
                                          </div>
                                          <div className="flex items-center gap-3 mt-1.5 text-white/40 text-xs flex-wrap">
                                            <span className="flex items-center gap-1">
                                              <Calendar className="w-3 h-3" />
                                              {formatDate(trip.departure_date)}
                                            </span>
                                            <span>{frequencyLabels[trip.payment_frequency] || trip.payment_frequency}</span>
                                            <span>Total: {formatCurrency(trip.total_amount_cents)}</span>
                                          </div>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                          <p className="text-cyan-400 text-sm font-normal">
                                            Paid: {formatCurrency(trip.paid_amount_cents)}
                                          </p>
                                          <p className="text-white/50 text-xs">
                                            Left: {formatCurrency(trip.remaining_cents)}
                                          </p>
                                          {isTripExpanded ? (
                                            <ChevronDown className="w-4 h-4 text-white/40 mt-1" />
                                          ) : (
                                            <ChevronRight className="w-4 h-4 text-white/40 mt-1" />
                                          )}
                                        </div>
                                      </button>

                                      <AnimatePresence>
                                        {isTripExpanded && (
                                          <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="border-t border-white/10"
                                          >
                                            <div className="p-4 space-y-4">
                                              {/* Payment history (completed) */}
                                              {(trip.payment_history || []).length > 0 && (
                                                <div>
                                                  <h4 className="text-white/70 text-xs font-normal uppercase tracking-wider mb-2 flex items-center gap-2">
                                                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                                                    Payment History
                                                  </h4>
                                                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                                    {trip.payment_history.map((p, i) => (
                                                      <div
                                                        key={i}
                                                        className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-black/40 border border-white/5 text-sm"
                                                      >
                                                        <span className="text-emerald-400 font-medium">
                                                          {formatCurrency(p.amount_cents)}
                                                        </span>
                                                        <span className="text-white/50 text-xs">
                                                          {formatDateTime(p.date)}
                                                        </span>
                                                        {p.type === 'one_time' && (
                                                          <span className="text-[10px] text-white/40">early</span>
                                                        )}
                                                      </div>
                                                    ))}
                                                  </div>
                                                </div>
                                              )}

                                              {/* Upcoming schedule */}
                                              {(trip.schedule || []).length > 0 && (
                                                <div>
                                                  <h4 className="text-white/70 text-xs font-normal uppercase tracking-wider mb-2 flex items-center gap-2">
                                                    <Clock className="w-3.5 h-3.5 text-brand-400" />
                                                    Upcoming Payments ({trip.per_payment_cents ? formatCurrency(trip.per_payment_cents) : '—'} each)
                                                  </h4>
                                                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                                    {trip.schedule.map((s, i) => {
                                                      const isPast = isBefore(new Date(s.date), new Date());
                                                      return (
                                                        <div
                                                          key={i}
                                                          className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm ${
                                                            isPast
                                                              ? 'bg-white/5 border-white/10 text-white/50'
                                                              : 'bg-brand-500/5 border-brand-500/20 text-white/85'
                                                          }`}
                                                        >
                                                          <span className="font-medium">
                                                            {formatCurrency(s.amount_cents)}
                                                          </span>
                                                          <span className="text-xs">
                                                            {formatDate(s.date)}
                                                            {isPast && ' (past)'}
                                                          </span>
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                              )}

                                              {trip.deadline_date && (
                                                <p className="text-white/40 text-xs">
                                                  Deadline: {formatDate(trip.deadline_date)}
                                                </p>
                                              )}

                                              <div className="pt-2 border-t border-white/10">
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  disabled={repairingId === trip.id || trip.subscription_status === 'stripe_unavailable'}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRepairSubscription(trip.id);
                                                  }}
                                                  className="w-full sm:w-auto border-brand-500/30 text-brand-200/90 hover:bg-brand-500/10 hover:text-brand-100"
                                                >
                                                  {repairingId === trip.id ? (
                                                    <>
                                                      <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                                                      Repairing…
                                                    </>
                                                  ) : (
                                                    <>
                                                      <Wrench className="w-3.5 h-3.5 mr-2" />
                                                      Repair Stripe subscription
                                                    </>
                                                  )}
                                                </Button>
                                                <p className="text-white/35 text-[10px] mt-2 leading-relaxed">
                                                  Syncs booking total to Stripe and sets the recurring price to match remaining balance + schedule. Use when amounts drift.
                                                </p>
                                              </div>
                                            </div>
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </div>
                </>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
